<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-3xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold" v-if="updating === true">
                                                Update Bank Account `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white font-bold" v-else>
                                                New Bank Account
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white" v-if="updating === false">
                                                Get started by filling in the information below to create a new
                                                bank account.
                                            </p>
                                            <p class="text-sm text-white" v-else>
                                                Make any changes to the bank account by updating the information below.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="pt-6 pb-5 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Bank Account
                                                        Information</h3>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name" v-model="form.name"
                                                               placeholder="Enter the office name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="bank_name"
                                                           class="block text-sm text-gray-900">
                                                        Bank Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.bank_name" id="bank_name"
                                                               placeholder="Enter a bank name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.bank_name}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="account_number"
                                                           class="block text-sm text-gray-900">
                                                        Account Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.account_number"
                                                               id="account_number"
                                                               placeholder="Enter the account number"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.account_number}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="sort_code"
                                                           class="block text-sm text-gray-900">
                                                        Sort Code
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.sort_code"
                                                               id="account_number"
                                                               placeholder="Enter the sort code"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.sort_code}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="iban"
                                                           class="block text-sm text-gray-900">
                                                        IBAN
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.iban" id="iban"
                                                               placeholder="Enter the IBAN"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.iban}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="bic"
                                                           class="block text-sm text-gray-900">
                                                        BIC
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.bic" id="bic"
                                                               placeholder="Enter the BIC"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.bic}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="vat_number"
                                                           class="block text-sm text-gray-900">
                                                        VAT Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.vat_number" id="vat_number"
                                                               placeholder="Enter a VAT number"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.vat_number}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="role"
                                                           class="block text-sm text-gray-900">
                                                        Currency
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="role" name="role" v-model="form.currency_id"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Select a currency</option>
                                                            <option v-for="currency in currencies" :value="currency.id"
                                                                    v-html="currency.name"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, ExclamationCircleIcon} from '@heroicons/vue/solid'
import {useForm} from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {mapState} from "vuex";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        ExclamationCircleIcon,
        XIcon,
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const updating = false;

        const form = useForm({
            id: null,
            name: null,
            bank_name: null,
            account_number: null,
            sort_code: null,
            iban: null,
            bic: null,
            vat_number: null,
            currency_id : ''
        })

        function submit() {
            if (this.updating) {
                this.form.post(route('admin.bank-accounts.bank-account.update', this.form.id), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('admin.bank-accounts.store'), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        }

        function close() {
            this.show = false;
            this.form.reset();
            this.form.clearErrors();
        }

        function open() {
            this.$store.dispatch('bankaccounts/getData')
            this.form.clearErrors();
            this.show = true;
        }

        return {
            show,
            form,
            submit,
            updating,
            close,
            open,
            showErrors
        }
    },
    computed: {
        ...mapState({
            currencies: state => state.bankaccounts.currencies ?? null,
        }),
    }
}
</script>
